import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Copy, Trash2, Image as ImageIcon, RotateCw, Video, Boxes, Loader2, Search, X, Tag as TagIcon, Check, Music } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "all",           label: "All",            icon: Boxes,     accent: "text-[#1a3c5e]" },
  { value: "images",        label: "Images",         icon: ImageIcon, accent: "text-emerald-600" },
  { value: "pano360",       label: "360° Panoramas", icon: RotateCw,  accent: "text-blue-600" },
  { value: "videos",        label: "Videos",         icon: Video,     accent: "text-red-600" },
  { value: "package-media", label: "Package Media",  icon: Boxes,     accent: "text-[#F7941D]" },
  { value: "audio",         label: "Audio",          icon: Music,     accent: "text-purple-600" },
];

function humanSize(b) {
  if (!b) return "—";
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
  return (b / 1024 / 1024).toFixed(1) + " MB";
}

function fileToCategory(name) {
  const ext = name.split(".").pop().toLowerCase();
  if (["mp4", "webm", "mov"].includes(ext)) return "videos";
  return "images";
}

export default function MediaLibrary({ pickMode = false, onPick }) {
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get(`/admin/media?category=${cat}&search=${encodeURIComponent(search)}`),
        api.get(`/admin/media/stats`),
      ]);
      setItems(list.data);
      setStats(st.data);
    } catch {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  }, [cat, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const uploadFiles = useCallback(async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setProgress(0);
    const total = files.length;
    let done = 0;
    const targetCat = cat === "all" ? null : cat;
    for (const f of files) {
      const finalCat = targetCat || fileToCategory(f.name);
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", finalCat);
      try {
        await api.post("/admin/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
      done += 1;
      setProgress(Math.round((done / total) * 100));
    }
    setUploading(false);
    setProgress(0);
    toast.success(`Uploaded ${done}/${total} files`);
    load();
  }, [cat, load]);

  const onFileInput = (e) => {
    uploadFiles(Array.from(e.target.files || []));
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const copyUrl = (item) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = base + item.public_url;
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("URL copied");
      } catch {
        toast.message("Long-press to copy URL", { description: url });
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => toast.success("URL copied"),
        () => fallback(),
      );
    } else {
      fallback();
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.file_name}?`)) return;
    try {
      await api.delete(`/admin/media/${item.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="media-library">
      {!pickMode && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Media Library</h2>
            <p className="text-xs text-gray-500 mt-0.5">Upload, organize and reuse media across packages, blog and site.</p>
          </div>
          {stats && (
            <div className="text-xs text-gray-500">
              <b className="text-[#1a3c5e]">{stats.all.count}</b> files · <b className="text-[#1a3c5e]">{humanSize(stats.all.total_size)}</b> used
            </div>
          )}
        </div>
      )}

      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="media-dropzone"
        className={`rounded-2xl border-2 border-dashed p-6 cursor-pointer transition-colors ${drag ? "border-[#F7941D] bg-[#FEF3E2]/60" : "border-[#1a3c5e]/20 bg-white hover:bg-[#FEF3E2]/30"}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/mp4,video/webm,video/quicktime"
          onChange={onFileInput}
          className="hidden"
          data-testid="media-file-input"
        />
        <div className="flex flex-col items-center gap-2 text-center">
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-[#F7941D] animate-spin" />
              <div className="text-sm font-bold text-[#1a3c5e]">Uploading... {progress}%</div>
              <div className="w-full max-w-md h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-[#F7941D] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </>
          ) : (
            <>
              <Upload className="w-8 h-8 text-[#F7941D]" />
              <div className="text-sm font-bold text-[#1a3c5e]">Drop files here or click to upload</div>
              <div className="text-[11px] text-gray-500">JPG · PNG · WebP · GIF · MP4 · WebM · MOV (max 25MB · auto-compressed)</div>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          const count = stats ? (stats[c.value]?.count ?? stats.all?.count ?? 0) : 0;
          return (
            <button
              key={c.value}
              onClick={() => setCat(c.value)}
              data-testid={`media-cat-${c.value}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${cat === c.value ? "bg-[#1a3c5e] text-white" : "bg-white border border-[#1a3c5e]/15 text-[#1a3c5e] hover:bg-[#FEF3E2]/40"}`}
            >
              <Icon className={`w-3.5 h-3.5 ${cat === c.value ? "text-white" : c.accent}`} />
              {c.label}
              <span className={`text-[10px] ${cat === c.value ? "text-white/80" : "text-gray-400"}`}>·{count}</span>
            </button>
          );
        })}
        <div className="ml-auto relative">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search filename or tag..."
            data-testid="media-search"
            className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-gray-400 text-sm">No media yet. Drop a file above to get started.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="media-grid">
          {items.map((it) => (
            <MediaCard key={it.id} item={it} onCopy={copyUrl} onDelete={remove} pickMode={pickMode} onPick={onPick} />
          ))}
        </div>
      )}
    </div>
  );
}

function MediaCard({ item, onCopy, onDelete, pickMode, onPick }) {
  const isVideo = (item.content_type || "").startsWith("video/");
  const isPano = item.category === "pano360";
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const url = base + item.public_url;
  return (
    <div className="group bg-white rounded-xl border border-[#1a3c5e]/10 overflow-hidden flex flex-col" data-testid={`media-card-${item.id}`}>
      <div className="relative aspect-square bg-gray-100">
        {isVideo ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-red-100 to-red-50 text-red-700">
            <Video className="w-8 h-8" />
            <div className="text-[10px] mt-1 font-bold uppercase">Video</div>
          </div>
        ) : (
          <img src={url} alt={item.file_name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {isPano && (
          <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-blue-600 text-white rounded text-[9px] font-bold uppercase flex items-center gap-0.5">
            <RotateCw className="w-2.5 h-2.5" /> 360°
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          {pickMode ? (
            <button
              onClick={(e) => { e.stopPropagation(); onPick?.(url, item); }}
              data-testid={`media-pick-${item.id}`}
              className="px-3 py-1.5 bg-[#F7941D] hover:bg-[#e0861a] text-white rounded-lg text-xs font-bold flex items-center gap-1"
            >
              <Check className="w-3.5 h-3.5" /> Pick
            </button>
          ) : (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onCopy(item); }}
                data-testid={`media-copy-${item.id}`}
                className="p-2 bg-white rounded-lg hover:bg-[#FEF3E2]/80"
                title="Copy URL"
              ><Copy className="w-4 h-4 text-[#1a3c5e]" /></button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(item); }}
                data-testid={`media-delete-${item.id}`}
                className="p-2 bg-white rounded-lg hover:bg-red-50"
                title="Delete"
              ><Trash2 className="w-4 h-4 text-red-600" /></button>
            </>
          )}
        </div>
      </div>
      <div className="p-2">
        <div className="text-[11px] font-semibold text-[#1a3c5e] truncate" title={item.file_name}>{item.file_name}</div>
        <div className="text-[10px] text-gray-500 flex justify-between">
          <span>{humanSize(item.size)}</span>
          {item.width > 0 && <span>{item.width}×{item.height}</span>}
        </div>
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-0.5 mt-1">
            {item.tags.slice(0, 2).map((t, i) => (
              <span key={i} className="inline-flex items-center text-[9px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                <TagIcon className="w-2 h-2 mr-0.5" />{t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Modal-style picker — drop-in for inputs that need a media library reference.
 * Usage:
 *   <MediaPickerModal open={true} category="pano360" onClose={...} onPick={(url) => ...} />
 */
export function MediaPickerModal({ open, category = "all", onClose, onPick }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="font-display text-lg font-bold text-[#1a3c5e]">Pick from Media Library</div>
          <button onClick={onClose} data-testid="media-picker-close" className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <MediaLibrary pickMode={true} onPick={(url, item) => { onPick?.(url, item); onClose?.(); }} />
        </div>
      </div>
    </div>
  );
}
